import { useTranslation } from "react-i18next";
import { appPaths } from "../../../../shared/routing/appPaths";
import { WorkspaceLink } from "../../../../shell/workspace";
import AppButton from "../../../../ui/buttons/AppButton";
import { spaced } from "../../../workflows/inputCards/typeWords";
import { editIntent, useHerePath, useThreadPreparation } from "../../prepare";
import { useThreadContext } from "../threadPageContext";
import type { StepRead } from "../currentStep/stepUses";
import type { StepGraphNode } from "../../../workflows/model/workflowGraph";
import { stepPortLabel } from "../../../workflows/model/stepPortLabel";

interface StepReadsProps {
  readonly reads: readonly StepRead[];
  readonly node: StepGraphNode;
  /** The project's bindings artifact, where a role's item is changed; `null` when none was read. */
  readonly bindings: string | null;
}

/**
 * What a step reads from the project (F103): the guideline, the label list, the corpus
 * description, each opening beside the Thread in the split view, so it can be read while the
 * step's result is reviewed (F104). Items the project's bindings choose (PG-5) are changed
 * in the bindings' edit Thread, the one place that choice is made.
 */
export default function StepReads({ reads, node, bindings }: StepReadsProps) {
  const { t, i18n } = useTranslation();
  const { overview } = useThreadContext();
  const preparation = useThreadPreparation();
  const here = useHerePath();
  if (reads.length === 0) return null;
  const bound = reads.filter((read) => read.role !== null);
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs">
      <span className="app-text-muted">{t("threads.steps.reads")}</span>
      {reads.map((read) => {
        const label = stepPortLabel(node, read.slot, "input", t, i18n.resolvedLanguage);
        const slot = label ?? `\`${read.slot}\``;
        return (
          <WorkspaceLink
            key={`${read.slot}:${read.reference}`}
            route={appPaths.item(read.reference)}
            placement="beside"
            className="app-link app-link-cue-away underline"
            title={read.role === null ? slot : t("threads.steps.boundBy", { slot, role: spaced(read.role) })}
          >
            {read.name}
          </WorkspaceLink>
        );
      })}
      {bound.length === 0 || bindings === null ? null : (
        <AppButton
          variant="ghost"
          size="xs"
          title={t("threads.steps.changeBoundHint", { items: bound.map((read) => read.name).join(", ") })}
          onClick={() => { preparation.prepare(editIntent(overview.projectId, { itemKind: "artifact", name: t("threads.steps.bindingsName"), reference: bindings }, here)); }}
        >
          {t("threads.steps.changeBound")}
        </AppButton>
      )}
    </div>
  );
}
